import React, { useEffect, useState, useCallback } from "react";
import { Calendar, Clock, Server, Play, Save, CheckCircle2, AlertTriangle, Eye, EyeOff, Wifi } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../services/api";

const CRON_PRESETS = [
    { label: "Denne 3:00",    value: "0 3 * * *" },
    { label: "Týždenne (Po)", value: "0 3 * * 1" },
    { label: "Mesačne (1.)",  value: "0 3 1 * *" },
    { label: "Vlastný",       value: "__custom__"  },
];

const CRON_DESCRIPTIONS = {
    "0 3 * * *": "Každý deň o 3:00",
    "0 3 * * 1": "Každý pondelok o 3:00",
    "0 3 1 * *": "Prvého dňa každého mesiaca o 3:00",
};

function defaultPort(type) {
    return { webdav: 80, ftp: 21, sftp: 22 }[type] || 0;
}

function fmtDate(ts) {
    if (!ts) return null;
    try {
        return new Date(ts).toLocaleString("sk-SK", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit", second: "2-digit",
        });
    } catch { return ts; }
}

const EMPTY = {
    enabled: false,
    cron: "0 3 * * *",
    keep_last: 7,
    nas_enabled: false,
    nas_type: "webdav",
    nas_host: "",
    nas_port: 0,
    nas_username: "",
    nas_password: "",
    nas_path: "/backups",
    nas_ssl: false,
    last_backup: "",
    last_error: "",
};

function Toggle({ checked, onChange }) {
    return (
        <button
            type="button"
            onClick={() => onChange(!checked)}
            className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${checked ? "bg-nova-cyan" : "bg-zinc-700"}`}
        >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-5" : "translate-x-0"}`} />
        </button>
    );
}

function Field({ label, children, hint }) {
    return (
        <div>
            <label className="block text-xs text-zinc-400 font-mono uppercase tracking-wider mb-1.5">{label}</label>
            {children}
            {hint && <div className="text-zinc-600 text-xs mt-1">{hint}</div>}
        </div>
    );
}

function Input({ className = "", ...props }) {
    return (
        <input
            className={`w-full px-3 py-2 bg-nova-black/40 border border-white/10 rounded-lg text-white text-sm focus:border-nova-cyan/50 focus:outline-none transition-colors ${className}`}
            {...props}
        />
    );
}

export default function AdminBackupSchedule() {
    const [cfg, setCfg]         = useState(EMPTY);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving]   = useState(false);
    const [testing, setTesting] = useState(false);
    const [running, setRunning] = useState(false);
    const [showPass, setShowPass] = useState(false);

    const isPreset = CRON_PRESETS.some(p => p.value !== "__custom__" && p.value === cfg.cron);

    const load = useCallback(async () => {
        setLoading(true);
        try { setCfg(await api.getBackupSchedule()); }
        catch (e) { toast.error(e.message); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const set = (key, val) => setCfg(prev => ({ ...prev, [key]: val }));

    const handleSave = async () => {
        setSaving(true);
        try {
            setCfg(await api.saveBackupSchedule(cfg));
            toast.success("Nastavenia uložené");
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleTestNas = async () => {
        setTesting(true);
        try {
            await api.saveBackupSchedule(cfg);
            const r = await api.testNas();
            r.ok ? toast.success("Pripojenie k NAS úspešné") : toast.error("NAS chyba: " + r.error);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setTesting(false);
        }
    };

    const handleRunNow = async () => {
        setRunning(true);
        try {
            const r = await api.triggerBackupNow();
            toast.success(`Záloha vytvorená: ${r.name}`);
            await load();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setRunning(false);
        }
    };

    if (loading) return (
        <div className="space-y-6 animate-pulse max-w-2xl">
            <div className="h-8 w-56 bg-nova-surface/60 rounded-lg" />
            <div className="h-52 bg-nova-surface/40 rounded-2xl" />
            <div className="h-72 bg-nova-surface/40 rounded-2xl" />
        </div>
    );

    return (
        <div className="space-y-6 max-w-2xl">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="font-heading text-2xl font-black text-white tracking-tight">Automatické zálohy</h1>
                    <p className="text-zinc-500 text-sm mt-1">Plánované zálohovanie a odosielanie na NAS.</p>
                </div>
                <button
                    onClick={handleRunNow}
                    disabled={running}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/15 text-zinc-300 hover:text-white hover:border-white/30 text-sm font-medium transition-colors disabled:opacity-50"
                >
                    {running
                        ? <><span className="w-3.5 h-3.5 border-2 border-zinc-400/30 border-t-zinc-300 rounded-full animate-spin" /> Zálohujem…</>
                        : <><Play className="w-3.5 h-3.5" /> Zálohovať teraz</>
                    }
                </button>
            </div>

            {/* Last run status */}
            {(cfg.last_backup || cfg.last_error) && (
                <div className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-sm ${
                    cfg.last_error
                        ? "bg-red-500/8 border-red-500/20 text-red-300"
                        : "bg-green-500/8 border-green-500/20 text-green-300"
                }`}>
                    {cfg.last_error
                        ? <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        : <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                    }
                    <div>
                        {cfg.last_backup && <div>Posledná záloha: <span className="font-mono">{fmtDate(cfg.last_backup)}</span></div>}
                        {cfg.last_error  && <div className="text-xs mt-0.5 opacity-80">{cfg.last_error}</div>}
                    </div>
                </div>
            )}

            {/* Schedule */}
            <div className="bg-nova-surface/30 border border-white/8 rounded-2xl p-6 space-y-5">
                <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-nova-cyan" />
                    <h2 className="font-heading font-bold text-white">Plán zálohovania</h2>
                </div>

                <label className="flex items-center justify-between gap-4 cursor-pointer">
                    <div>
                        <div className="text-white text-sm font-medium">Automatické zálohovanie</div>
                        <div className="text-zinc-500 text-xs mt-0.5">Spúšťa zálohu podľa nastaveného plánu</div>
                    </div>
                    <Toggle checked={cfg.enabled} onChange={v => set("enabled", v)} />
                </label>

                {cfg.enabled && (
                    <>
                        <Field label="Frekvencia">
                            <div className="flex flex-wrap gap-2">
                                {CRON_PRESETS.map(p => {
                                    const active = p.value === "__custom__"
                                        ? !isPreset
                                        : (isPreset && cfg.cron === p.value);
                                    return (
                                        <button
                                            key={p.value}
                                            onClick={() => {
                                                if (p.value !== "__custom__") set("cron", p.value);
                                                else if (isPreset) set("cron", "");
                                            }}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                                active
                                                    ? "bg-nova-cyan text-nova-black"
                                                    : "bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white"
                                            }`}
                                        >
                                            {p.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </Field>

                        {isPreset ? (
                            <div className="flex items-center gap-2 text-zinc-400 text-sm">
                                <Clock className="w-3.5 h-3.5" />
                                {CRON_DESCRIPTIONS[cfg.cron] || cfg.cron}
                            </div>
                        ) : (
                            <Field label="Cron výraz" hint="Formát: minúty hodiny deň mesiac deň_týždňa">
                                <Input
                                    type="text"
                                    value={cfg.cron}
                                    onChange={e => set("cron", e.target.value)}
                                    placeholder="0 3 * * *"
                                    className="font-mono"
                                />
                            </Field>
                        )}

                        <Field
                            label="Uchovať posledných zálohy"
                            hint="Staršie zálohy sa automaticky zmažú"
                        >
                            <div className="flex items-center gap-4 mt-1">
                                <input
                                    type="range" min="1" max="30"
                                    value={cfg.keep_last}
                                    onChange={e => set("keep_last", parseInt(e.target.value))}
                                    className="flex-1 accent-nova-cyan"
                                />
                                <span className="w-8 text-center font-mono text-white text-sm font-bold">{cfg.keep_last}</span>
                            </div>
                        </Field>
                    </>
                )}
            </div>

            {/* NAS */}
            <div className="bg-nova-surface/30 border border-white/8 rounded-2xl p-6 space-y-5">
                <div className="flex items-center gap-2">
                    <Server className="w-4 h-4 text-nova-cyan" />
                    <h2 className="font-heading font-bold text-white">NAS / Vzdialené úložisko</h2>
                </div>

                <label className="flex items-center justify-between gap-4 cursor-pointer">
                    <div>
                        <div className="text-white text-sm font-medium">Odosielať zálohy na NAS</div>
                        <div className="text-zinc-500 text-xs mt-0.5">Po každej zálohe odošle súbor na vzdialené úložisko</div>
                    </div>
                    <Toggle checked={cfg.nas_enabled} onChange={v => set("nas_enabled", v)} />
                </label>

                {cfg.nas_enabled && (
                    <div className="space-y-4 pt-1">
                        {/* Protocol */}
                        <Field label="Protokol">
                            <div className="flex gap-2">
                                {["webdav", "ftp", "sftp"].map(t => (
                                    <button
                                        key={t}
                                        onClick={() => setCfg(prev => {
                                            const autoPort = !prev.nas_port || prev.nas_port === defaultPort(prev.nas_type);
                                            return { ...prev, nas_type: t, nas_port: autoPort ? defaultPort(t) : prev.nas_port };
                                        })}
                                        className={`px-4 py-1.5 rounded-lg text-xs font-mono font-medium uppercase transition-colors ${
                                            cfg.nas_type === t
                                                ? "bg-nova-cyan text-nova-black"
                                                : "bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white"
                                        }`}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </Field>

                        {/* Host + Port */}
                        <div className="grid grid-cols-3 gap-3">
                            <div className="col-span-2">
                                <Field label="Hostiteľ / IP">
                                    <Input
                                        type="text"
                                        value={cfg.nas_host}
                                        onChange={e => set("nas_host", e.target.value)}
                                        placeholder="192.168.1.100"
                                    />
                                </Field>
                            </div>
                            <Field label="Port">
                                <Input
                                    type="number"
                                    value={cfg.nas_port || ""}
                                    onChange={e => set("nas_port", parseInt(e.target.value) || 0)}
                                    placeholder={String(defaultPort(cfg.nas_type))}
                                />
                            </Field>
                        </div>

                        {/* Username + Password */}
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Používateľ">
                                <Input
                                    type="text"
                                    value={cfg.nas_username}
                                    onChange={e => set("nas_username", e.target.value)}
                                    placeholder="admin"
                                    autoComplete="off"
                                />
                            </Field>
                            <Field label="Heslo">
                                <div className="relative">
                                    <Input
                                        type={showPass ? "text" : "password"}
                                        value={cfg.nas_password}
                                        onChange={e => set("nas_password", e.target.value)}
                                        placeholder="••••••••"
                                        autoComplete="new-password"
                                        className="pr-9"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPass(p => !p)}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                                    >
                                        {showPass ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                    </button>
                                </div>
                            </Field>
                        </div>

                        {/* Remote path */}
                        <Field label="Vzdialená cesta">
                            <Input
                                type="text"
                                value={cfg.nas_path}
                                onChange={e => set("nas_path", e.target.value)}
                                placeholder="/backups"
                                className="font-mono"
                            />
                        </Field>

                        {/* SSL (WebDAV only) */}
                        {cfg.nas_type === "webdav" && (
                            <label className="flex items-center gap-3 cursor-pointer">
                                <Toggle
                                    checked={cfg.nas_ssl}
                                    onChange={v => {
                                        set("nas_ssl", v);
                                        if (!cfg.nas_port || cfg.nas_port === 80 || cfg.nas_port === 443) {
                                            set("nas_port", v ? 443 : 80);
                                        }
                                    }}
                                />
                                <span className="text-sm text-white">HTTPS / SSL</span>
                            </label>
                        )}

                        {/* Test connection */}
                        <button
                            onClick={handleTestNas}
                            disabled={testing || !cfg.nas_host}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/15 text-zinc-300 hover:text-white hover:border-white/30 text-sm font-medium transition-colors disabled:opacity-40"
                        >
                            {testing
                                ? <><span className="w-3.5 h-3.5 border-2 border-zinc-400/30 border-t-zinc-300 rounded-full animate-spin" /> Testujem…</>
                                : <><Wifi className="w-3.5 h-3.5" /> Otestovať pripojenie</>
                            }
                        </button>
                    </div>
                )}
            </div>

            {/* Save */}
            <div className="flex justify-end pb-4">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-nova-cyan text-nova-black font-semibold text-sm hover:bg-nova-cyan/90 disabled:opacity-60 transition-colors"
                >
                    {saving
                        ? <><span className="w-4 h-4 border-2 border-nova-black/30 border-t-nova-black rounded-full animate-spin" /> Ukladám…</>
                        : <><Save className="w-4 h-4" /> Uložiť nastavenia</>
                    }
                </button>
            </div>
        </div>
    );
}
